import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Paper,
  CircularProgress
} from '@mui/material';
import {
  CheckCircle,
  Flight,
  Hotel,
  DirectionsCar,
  Phone,
  Person,
  Star,
  Verified
} from '@mui/icons-material';
import api from '../services/api';

const ServiceTierSelection = ({ onSelectTier, selectedTier }) => {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchServiceTiers();
  }, []);

  const fetchServiceTiers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/v1/travel/tiers');
      setTiers(response.data);
    } catch (err) {
      console.error('Error fetching service tiers:', err);
      setError('Failed to load service tiers');
    } finally {
      setLoading(false);
    }
  };

  const getTierIcon = (tierCode) => {
    switch (tierCode) {
      case 'DIGITAL':
        return <Flight sx={{ fontSize: 48, color: '#1A5EA8' }} />;
      case 'CONCIERGE':
        return <Person sx={{ fontSize: 48, color: '#10b981' }} />;
      case 'WHITE_GLOVE':
        return <Star sx={{ fontSize: 48, color: '#f59e0b' }} />;
      default:
        return <Flight sx={{ fontSize: 48 }} />;
    }
  };

  const getTierColor = (tierCode) => {
    switch (tierCode) {
      case 'DIGITAL':
        return '#1A5EA8';
      case 'CONCIERGE':
        return '#10b981';
      case 'WHITE_GLOVE':
        return '#f59e0b';
      default:
        return '#1A5EA8';
    }
  };

  const getTierRecommendation = (tierCode) => {
    switch (tierCode) {
      case 'DIGITAL':
        return 'Best for independent travelers';
      case 'CONCIERGE':
        return 'Most popular choice';
      case 'WHITE_GLOVE':
        return 'Premium experience';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography color="error">{error}</Typography>
        <Button onClick={fetchServiceTiers} sx={{ mt: 2 }}>
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
          Choose Your Travel Experience
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
          Select the service level that's right for you
        </Typography>
      </Box>

      {/* Tier Cards */}
      <Grid container spacing={4}>
        {tiers.map((tier) => (
          <Grid item xs={12} md={4} key={tier.id}>
            <Card
              elevation={selectedTier === tier.tierCode ? 8 : 2}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                border: selectedTier === tier.tierCode ? `3px solid ${getTierColor(tier.tierCode)}` : '1px solid #e5e7eb',
                transition: 'all 0.3s',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 6
                }
              }}
              onClick={() => onSelectTier(tier.tierCode)}
            >
              {/* Popular Badge */}
              {tier.tierCode === 'CONCIERGE' && (
                <Chip
                  label="MOST POPULAR"
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    bgcolor: '#10b981',
                    color: 'white',
                    fontWeight: 600
                  }}
                />
              )}

              {/* Selected Badge */}
              {selectedTier === tier.tierCode && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 16,
                    left: 16,
                    bgcolor: getTierColor(tier.tierCode),
                    color: 'white',
                    borderRadius: '50%',
                    p: 0.5
                  }}
                >
                  <CheckCircle />
                </Box>
              )}

              <CardContent sx={{ flexGrow: 1, pt: 5 }}>
                {/* Icon */}
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  {getTierIcon(tier.tierCode)}
                </Box>

                {/* Tier Name */}
                <Typography
                  variant="h5"
                  align="center"
                  gutterBottom
                  sx={{ fontWeight: 700, color: getTierColor(tier.tierCode) }}
                >
                  {tier.tierName}
                </Typography>

                {/* Recommendation */}
                <Typography
                  variant="caption"
                  align="center"
                  display="block"
                  sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}
                >
                  {getTierRecommendation(tier.tierCode)}
                </Typography>

                {/* Price */}
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: getTierColor(tier.tierCode) }}>
                    ${tier.basePrice.toFixed(0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    per trip
                  </Typography>
                </Box>

                {/* Description */}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                  sx={{ mb: 3 }}
                >
                  {tier.tierDescription}
                </Typography>

                {/* Features List */}
                <List dense>
                  {tier.featuresIncluded.map((feature, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircle sx={{ fontSize: 20, color: getTierColor(tier.tierCode) }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={feature}
                        primaryTypographyProps={{
                          variant: 'body2',
                          color: 'text.primary'
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>

              <CardActions sx={{ p: 2, pt: 0 }}>
                <Button
                  fullWidth
                  variant={selectedTier === tier.tierCode ? 'contained' : 'outlined'}
                  size="large"
                  onClick={() => onSelectTier(tier.tierCode)}
                  sx={{
                    bgcolor: selectedTier === tier.tierCode ? getTierColor(tier.tierCode) : 'transparent',
                    borderColor: getTierColor(tier.tierCode),
                    color: selectedTier === tier.tierCode ? 'white' : getTierColor(tier.tierCode),
                    '&:hover': {
                      bgcolor: selectedTier === tier.tierCode ? getTierColor(tier.tierCode) : 'rgba(102, 126, 234, 0.1)',
                      borderColor: getTierColor(tier.tierCode)
                    }
                  }}
                >
                  {selectedTier === tier.tierCode ? 'Selected' : 'Select This Tier'}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Trust Signals */}
      <Paper
        elevation={0}
        sx={{
          mt: 6,
          p: 4,
          bgcolor: '#f9fafb',
          borderRadius: 3
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Verified sx={{ fontSize: 48, color: '#1A5EA8', mb: 1 }} />
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Secure Payment
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your payment information is encrypted and secure
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Phone sx={{ fontSize: 48, color: '#10b981', mb: 1 }} />
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                24/7 Support
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Help is always available when you need it
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 48, color: '#f59e0b', mb: 1 }} />
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Easy Cancellation
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Flexible cancellation policy for peace of mind
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default ServiceTierSelection;